import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Apartments.css';

import { supabase } from '../supabaseClient';
import { isPriceInBudget } from '../utils/priceParser';

export default function Apartments() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedProject, setSelectedProject] = useState(null);
  const [properties, setProperties] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApartments = async () => {
      setLoading(true);
      const { data } = await supabase.from('properties').select('*').eq('category', 'apartments');
      if (data) {
        setProperties(data);
        setFilteredProjects(data);
      }
      setLoading(false);
    };
    fetchApartments();
  }, []);

  useEffect(() => {
    if (location.state && properties.length > 0) {
      const { type, budget, location: locOption } = location.state;
      let newProjects = properties;

      if (type && type !== 'Any Type') {
        newProjects = newProjects.filter(p => p.sizes === type || p.bhk === type || p.title.includes(type));
      }
      
      if (locOption && locOption !== 'All Locations') {
        newProjects = newProjects.filter(p => p.location && p.location.includes(locOption));
      }

      if (budget) {
        newProjects = newProjects.filter(p => isPriceInBudget(p.price, budget));
      }

      setFilteredProjects(newProjects);
    }
  }, [location.state, properties]);

  return (
    <div className="apartments-page">
      {/* Page Header */}
      <section className="page-header animate-fade-in-up">
        <div className="container text-center">
          <span className="section-tag">Luxury Portfolios</span>
          <h1 className="page-title">Premium Apartments</h1>
          <p className="page-subtitle">
            Explore our curated selection of luxury high rise and low density flats in the most sought after prime locations.
          </p>
        </div>
      </section>

      {/* Cards Grid */}
      <section className="apartments-grid-section">
        <div className="container">
          <div className="grid-3" style={{ marginTop: '40px', alignItems: 'stretch' }}>
            {loading ? (
              <div style={{ textAlign: 'center', gridColumn: '1 / -1', padding: '40px 0' }}>
                <h3 style={{ color: 'var(--text-secondary)' }}>Loading...</h3>
              </div>
            ) : filteredProjects.length > 0 ? filteredProjects.map((project) => (
              <div key={project.id} className="premium-card apartment-card flex-column" onClick={() => setSelectedProject(project)} style={{ cursor: 'pointer' }}>
                <div className="apartment-card-image-container">
                  <img src={project.image_url || project.image} alt={project.title} className="apartment-card-image" />
                  <div className="apartment-card-badges">
                    <span className="approval-badge">✓ {project.approval}</span>
                    <span className={`status-badge status-${(project.status || '').replace(/ /g, '-').toLowerCase()}`} style={project.status === "Ready to Move In" ? { backgroundColor: '#ffca28', color: '#333', fontWeight: 'bold' } : {}}>
                      {project.status}
                    </span>
                  </div>
                </div>

                <div className="apartment-card-body">
                  <span className="project-type" style={{ marginBottom: '5px', display: 'block' }}>{project.type}</span>
                  <h2 className="project-title">{project.title}</h2>
                  <p className="project-location">📍 {project.location}</p>

                  <div className="project-specs">
                    <div className="spec-item">
                      <span className="spec-label">Configurations</span>
                      <span className="spec-val">{project.bhk || project.sizes}</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Area</span>
                      <span className="spec-val">{project.size || project.sizes}</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Pricing</span>
                      <span className="spec-val highlight-val">{project.price}</span>
                    </div>
                  </div>

                  <div className="apartment-card-footer" style={{ marginTop: 'auto', paddingTop: '15px' }}>
                    <button
                      className="btn btn-primary w-full"
                      style={{ marginBottom: '8px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProject(project);
                      }}
                    >
                      View Complete Info
                    </button>
                    <button
                      className="btn btn-secondary w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.localStorage.setItem('enquiry_subject', `Enquiry about ${project.title}`);
                        navigate('/contact');
                      }}
                    >
                      Check Availability & Prices
                    </button>
                  </div>
                </div>
              </div>
            )) : (
              <div style={{ textAlign: 'center', gridColumn: '1 / -1', padding: '40px 0' }}>
                <h3 style={{ color: 'var(--text-secondary)' }}>No apartments found matching your criteria.</h3>
                <button className="btn btn-primary" style={{ marginTop: '15px' }} onClick={() => setFilteredProjects(properties)}>View All Apartments</button>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* Luxury Standard Statement */}
      <section className="standards-banner">
        <div className="container">
          <div className="grid-2">
            <div>
              <span className="section-tag" style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}>
                Engineering Pride
              </span>
              <h2 style={{ color: 'white', fontSize: '2.2rem', marginTop: '10px' }}>
                Every Skyon Apartment adheres to the highest level of craftsmanship.
              </h2>
            </div>
            <div>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.1rem', marginBottom: '20px' }}>
                We use premium concrete grades, steel from top manufacturers, copper wiring of highest specifications, and sanitary fittings of international brands (Kohler/Grohe). Our home structural components carry a solid warranty.
              </p>
              <button 
                className="btn btn-secondary" 
                style={{ color: 'white', borderColor: 'white' }}
                onClick={() => navigate('/contact')}
              >
                Request Technical Specs Sheets
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedProject && (
        <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
          <div className="modal-content animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedProject(null)}>&times;</button>
            <img src={selectedProject.image_url || selectedProject.image} alt={selectedProject.title} className="modal-image" />
            
            <div className="modal-body">
              <h2 className="modal-title">{selectedProject.title}</h2>
              <p className="modal-tagline">{selectedProject.tagline}</p>
              
              {selectedProject.amenities?.length > 0 && (
                <div className="modal-section">
                  <h3>Amenities & Features</h3>
                  <ul className="grid-list">
                    {selectedProject.amenities.map((feat, i) => <li key={i}>▪ {feat}</li>)}
                  </ul>
                </div>
              )}

              {selectedProject.extended_details?.locationHighlights?.length > 0 && (
                <div className="modal-section">
                  <h3>Location & Connectivity</h3>
                  <ul className="grid-list">
                    {selectedProject.extended_details.locationHighlights.map((loc, i) => <li key={i}>📍 {loc}</li>)}
                  </ul>
                </div>
              )}

              <div className="modal-footer text-center">
                <button
                  className="btn btn-secondary w-full"
                  onClick={() => {
                    window.localStorage.setItem('enquiry_subject', `Enquiry about ${selectedProject.title}`);
                    setSelectedProject(null);
                    navigate('/contact');
                  }}
                >
                  Enquire Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
